import { useEffect, useRef, useState } from "react";
import { SortFilterBar } from "components/discount-achiever/components/SortFilterBar";
import { ProductCard } from "components/discount-achiever/components/ProductCard";
import { BlockNavRail } from "components/discount-achiever/components/BlockNavRail";
import { SelectionFooter } from "components/discount-achiever/components/SelectionFooter";
import { SortSheet, getSortLabel } from "components/discount-achiever/components/SortSheet";
import { FilterSheet } from "components/discount-achiever/components/FilterSheet";
import { ProgressBar } from "./ProgressBar";
import { Icon } from "@shopflo/ui/icon";
import { DiscountSealIcon } from "./DiscountSealIcon";
import { SuccessSealIcon } from "./SuccessSealIcon";
import { CaretLeft } from "@phosphor-icons/react";
import { motion, useReducedMotion } from "motion/react";
import { useDiscountAchieverContext } from "lib/contexts/DiscountAchieverProvider";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents } from "lib/utils/constants";
import {
  BlockNavRailItem,
  DiscountAchieverProduct,
  DiscountAchieverVariant,
  getInventoryCap,
  SortBy,
} from "lib/types/discountAchiever";
import {
  computeBlockFulfillment,
  getBlockRemainingMessage,
  getRemainingToShopMessage,
} from "lib/utils/discountAchieverFulfillment";
import { classNames } from "lib/utils/helpers";

// Card + cap (visual mute only). Multi-variant cards open the variant selector, so leave uncapped.
const getCardMaxQuantity = (product: DiscountAchieverProduct): number | undefined => {
  const variant = product.variants.length === 1 ? product.variants[0] : undefined;
  return variant ? getInventoryCap(variant) : undefined;
};
const getPrimaryImage = (product: DiscountAchieverProduct): string =>
  product.images.find((image) => image.is_primary)?.src ?? product.images[0]?.src ?? "";

const getVariantPricing = (variant?: DiscountAchieverVariant) => {
  if (!variant)
    return { price: 0, compareAtPrice: undefined, discountPercentage: undefined, currency: "INR" };
  const price = variant.discounted_price ?? variant.current_price;
  const compareAtPrice = variant.original_price > price ? variant.original_price : undefined;
  const discountPercentage = compareAtPrice
    ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
    : undefined;
  return { price, compareAtPrice, discountPercentage, currency: variant.currency || "INR" };
};

const getCardPricing = (product: DiscountAchieverProduct) => getVariantPricing(product.variants[0]);

// A product can have qty selected against any of its variants (picked via the variant selector,
// not just variants[0]) — the grid card's badge needs the sum across all of them.
const getProductSelectedQty = (product: DiscountAchieverProduct, selectedItems: Record<string, number>) =>
  product.variants.reduce((sum, variant) => sum + (selectedItems[variant.variant_id] ?? 0), 0);

const HEADER_IDLE_BG =
  "radial-gradient(100% 90.13% at 9.87% 0%, var(--flo-primary-with-alpha-2) 0%, transparent 100%)";
const HEADER_SUCCESS_BG =
  "radial-gradient(100% 90.13% at 9.87% 0%, rgba(60, 189, 129, 0.30) 0%, rgba(60, 189, 129, 0.00) 100%)";
const TILE_IDLE_BG =
  "conic-gradient(from 180deg at 50% 50%, color-mix(in srgb, var(--flo-primary-dark-color) 15%, transparent) 0deg, color-mix(in srgb, var(--flo-primary-dark-color) 15%, transparent) 40.66757261753082deg, transparent 72.75548815727234deg, color-mix(in srgb, var(--flo-primary-dark-color) 7%, transparent) 92.27437376976013deg, transparent 119.9891459941864deg, var(--flo-primary-lighter-color) 141.0123646259308deg, color-mix(in srgb, var(--flo-primary-dark-color) 15%, transparent) 360deg)";
// Same conic structure as the idle tile, recolored to the success green family
const TILE_SUCCESS_BG =
  "conic-gradient(from 180deg at 50% 50%, rgba(60, 189, 129, 0.15) 0deg, rgba(60, 189, 129, 0.15) 40.66757261753082deg, transparent 72.75548815727234deg, rgba(60, 189, 129, 0.07) 92.27437376976013deg, transparent 119.9891459941864deg, rgba(60, 189, 129, 0.25) 141.0123646259308deg, rgba(60, 189, 129, 0.15) 360deg)";

const ProductGridSkeleton = ({ columns }: { columns: number }) => (
  <div className={`grid gap-4 px-3 w-full ${columns === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
    {Array.from({ length: 6 }).map((_, index) => (
      <div key={index} className="flex flex-col gap-2 w-full">
        <div className="skeleton-loader h-[120px] w-full rounded-xl !bg-gray-100"></div>
        <div className="skeleton-loader h-[14px] w-full rounded-md !bg-gray-100"></div>
        <div className="skeleton-loader h-[14px] w-2/3 rounded-md !bg-gray-100"></div>
      </div>
    ))}
  </div>
);

interface ProductSelectionStepProps {
  onIncrement: () => void;
  onViewProduct: () => void;
  /** Route to the variant selector tray view (third view) */
  onOpenVariants: () => void;
  onNext: () => void;
  couponName?: string;
  /** Add-to-cart + discount apply in flight — footer CTA shows the dots loader */
  isSubmitting?: boolean;
}

export const ProductSelectionStep = ({
  onIncrement,
  onViewProduct,
  onOpenVariants,
  onNext,
  couponName,
  isSubmitting = false,
}: ProductSelectionStepProps) => {
  const [openSheet, setOpenSheet] = useState<"sort" | "filter" | null>(null);
  // Selected-items mode — footer deck tap swaps the grid to only the selected products
  const [showSelected, setShowSelected] = useState(false);
  const { state, actions } = useDiscountAchieverContext();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  // Recent images live in context — this step unmounts while the product viewer is open
  const { status, blocks, activeBlockIndex, recentSelections } = state;
  const isLoading = status === "loading" || status === "idle";
  const activeBlock = blocks[activeBlockIndex];
  const hasMultipleBlocks = blocks.length > 1;

  // Infinite scroll — sentinel at the grid's end fetches the next cursor page when it nears the viewport
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const hasMore = activeBlock?.block.pagination.has_more ?? false;
  const loadingMore = activeBlock?.loadingMore ?? false;

  const pageError = activeBlock?.pageError ?? null;

  // Switching blocks swaps the grid contents — jump back to the top so the new block starts at row 1
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [activeBlockIndex]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    // pageError pauses auto-fetch — otherwise a failing API would retry in a loop; user retries by tap
    if (!sentinel || !hasMore || loadingMore || pageError) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) actions.fetchNextPage(activeBlockIndex);
      },
      { root: scrollRef.current, rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, pageError, activeBlockIndex, actions, status]);

  // Total selected quantity across ALL blocks — footer cart is global, not per-block
  const selectedCount = blocks.reduce(
    (total, blockState) => total + Object.values(blockState.selectedItems).reduce((sum, qty) => sum + qty, 0),
    0,
  );

  const activeFilter = activeBlock?.filter ?? {};
  const priceBand = activeFilter.selected_price_band;
  const priceFilterLabel = priceBand ? `Price: ${priceBand.min} - ${priceBand.max}` : undefined;

  const handleSortSelect = (sortBy: SortBy) => {
    setOpenSheet(null);
    if (sortBy !== activeBlock?.sortBy) actions.setSort(activeBlockIndex, sortBy);
  };

  const handleFilterApply = (min: number, max: number) => {
    setOpenSheet(null);
    actions.setFilter(activeBlockIndex, {
      ...activeFilter,
      selected_price_band: { min, max },
    });
  };

  const handleFilterClear = () => {
    const { selected_price_band, ...rest } = activeFilter;
    actions.setFilter(activeBlockIndex, rest);
  };

  const reducedMotion = useReducedMotion();

  const fulfillments = blocks.map((blockState) =>
    computeBlockFulfillment(blockState.block, blockState.selectedItems,blockState.selectedMeta),
  );
  const isComplete = blocks.length > 0 && fulfillments.every((f) => f.isFulfilled);
  const remainingToShopMessage = getRemainingToShopMessage(
    blocks.map((blockState) => blockState.block),
    fulfillments,
  );
  const celebrateTransition = reducedMotion
    ? { duration: 0 }
    : { duration: 1, ease: [0.27, 0.01, 0, 1.01] as [number, number, number, number] };
  // Average fractional progress across blocks — amount blocks show intermediate progress, not just 0/100
  const progress =
    blocks.length > 0 ? (fulfillments.reduce((sum, f) => sum + f.progress, 0) / blocks.length) * 100 : 0;

  // Fire `unlocked` once when the whole tray becomes fulfilled (isComplete false→true). The ref holds
  // the previous edge so re-renders don't re-fire; only the real transition emits.
  const prevCompleteRef = useRef(false);
  useEffect(() => {
    if (isComplete && !prevCompleteRef.current) {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_DISCOUNT_ACHIEVER_UNLOCKED,
        eventType: "flo_action",
        metaData: {
          discountAchieverData: { couponName, blockCount: blocks.length, selectedCount },
        },
      });
    }
    prevCompleteRef.current = isComplete;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  // First unfulfilled block AFTER the active one, wrapping circularly (active itself excluded).
  // -1 when every other block is already fulfilled.
  const nextUnfulfilledBlock = (() => {
    for (let offset = 1; offset < blocks.length; offset++) {
      const index = (activeBlockIndex + offset) % blocks.length;
      if (!fulfillments[index].isFulfilled) return index;
    }
    return -1;
  })();

  // Footer CTA — submits (calls the API) ONLY when every block is fulfilled. Otherwise it walks
  // the shopper through the remaining blocks circularly; never adds to cart while short.
  const handleFooterCta = () => {
    if (isComplete) {
      onNext();
      return;
    }
    if (canGoToNextBlock) actions.setActiveBlock(nextUnfulfilledBlock);
    // else: the active block is still short — stay put, no submit until it's fulfilled
  };

  // The active block must be fulfilled before the shopper can move on — routing away while it's
  // still short would let them skip blocks and never reach a submittable state.
  const isActiveBlockFulfilled = fulfillments[activeBlockIndex]?.isFulfilled ?? false;
  const canGoToNextBlock = isActiveBlockFulfilled && nextUnfulfilledBlock !== -1;
  const footerEnabled = selectedCount > 0 && (isComplete || canGoToNextBlock);
  const footerCtaLabel = !isComplete && canGoToNextBlock ? "Go to next collection" : "Add Selected";

  const navItems: BlockNavRailItem[] = blocks.map((blockState, index) => ({
    id: String(index),
    thumbnail: blockState.block.products[0] ? getPrimaryImage(blockState.block.products[0]) : "",
    label: `Collection ${index + 1}`,
    current: fulfillments[index].current,
    target: fulfillments[index].target,
    showCount: blockState.block.impacted_entity.missing_amount === null,
  }));

  // Multi-variant products can't add inline — both +/- open the variant selector so the shopper
  // picks the specific variant/qty. Single-variant products add/remove variants[0] directly.
  const openVariantSelector = (product: DiscountAchieverProduct) => {
    actions.openDetail(product);
    onOpenVariants();
  };

  const handleAdd = (product: DiscountAchieverProduct) => {
    if (product.variants.length > 1) {
      openVariantSelector(product);
      return;
    }
    const variantId = product.variants[0]?.variant_id;
    if (variantId) actions.updateQty(activeBlockIndex, variantId, 1);
    onIncrement();
  };

  const handleRemove = (product: DiscountAchieverProduct) => {
    if (product.variants.length > 1) {
      openVariantSelector(product);
      return;
    }
    const variantId = product.variants[0]?.variant_id;
    if (variantId) actions.updateQty(activeBlockIndex, variantId, -1);
  };

  const getSelectedQty = (product: DiscountAchieverProduct) =>
    getProductSelectedQty(product, activeBlock?.selectedItems ?? {});

 // Selected variants → product + variant. Fall back to selectedMeta (add-time snapshot) so picks
  // dropped from the live list by a sort/filter refetch still render.
  const selectedProducts = blocks.flatMap((blockState, blockIndex) =>
    Object.entries(blockState.selectedItems).flatMap(([variantId, quantity]) => {
      const liveProduct = blockState.block.products.find((p) =>
        p.variants.some((v) => v.variant_id === variantId),
      );
      const liveVariant = liveProduct?.variants.find((v) => v.variant_id === variantId);
      const meta = blockState.selectedMeta[variantId];
      const product = liveProduct ?? meta?.product;
      const variant = liveVariant ?? meta?.variant;
      return product && variant ? [{ blockIndex, product, variant, quantity }] : [];
    }),
  );

  // Footer deck images, newest first. Resolved from the ordered selection keys (not stored as URLs)
  // so two variants of one product don't collapse into a single thumbnail that vanishes when only
  // one of them is removed. Same live-product → selectedMeta fallback as above.
  const recentImages = recentSelections.flatMap(({ blockIndex, variantId }) => {
    const blockState = blocks[blockIndex];
    if (!blockState) return [];
    const product =
      blockState.block.products.find((p) => p.variants.some((v) => v.variant_id === variantId)) ??
      blockState.selectedMeta[variantId]?.product;
    return product ? [getPrimaryImage(product)] : [];
  });


  // Nothing left selected — drop back to the full catalog view
  useEffect(() => {
    if (showSelected && selectedProducts.length === 0) setShowSelected(false);
  }, [showSelected, selectedProducts.length]);

  const handleSelectedQtyChange = (
    blockIndex: number,
    variant: DiscountAchieverVariant,
    delta: number,
  ) => {
    actions.updateQty(blockIndex, variant.variant_id, delta);
    if (delta > 0) onIncrement();
  };

  const renderProducts = () => {
    if (isLoading || activeBlock?.refreshing)
      return <ProductGridSkeleton columns={hasMultipleBlocks ? 2 : 3} />;

    if (status === "error") {
      return (
        <p className="px-4 py-8 w-full text-center text-text-sm-medium text-gray-dark">
          {state.error ?? "Failed to load products"}
        </p>
      );
    }

    if (!activeBlock || activeBlock.block.products.length === 0) {
      return (
        <p className="px-4 py-8 w-full text-center text-text-sm-medium text-gray-dark">
          No products available
        </p>
      );
    }

    return (
      <>
        {hasMultipleBlocks && (
          <p className="text-text-xs-semibold text-coal-medium px-5 py-1">
            {getBlockRemainingMessage(activeBlock.block, fulfillments[activeBlockIndex])}
          </p>
        )}
        <div className={`grid gap-4 px-4 w-full ${hasMultipleBlocks ? "grid-cols-2" : "grid-cols-3"}`}>
          {activeBlock.block.products.map((product) => (
            <ProductCard
              key={product.product_id}
              imageUrl={getPrimaryImage(product)}
              title={product.name}
              subtitle={product.variants[0]?.name}
              variantCount={product.variants?.length}
              {...getCardPricing(product)}
              onClick={() => {
                sendAnalyticsEvent({
                  eventName: analyticsEvents.FLO_DISCOUNT_ACHIEVER_PRODUCT_VIEWED,
                  eventType: "click",
                  metaData: {
                    discountAchieverData: { productId: product.product_id, blockIndex: activeBlockIndex },
                  },
                });
                actions.openDetail(product);
                onViewProduct();
              }}
              quantity={getSelectedQty(product)}
              maxQuantity={getCardMaxQuantity(product)}
              onAddClick={() => handleAdd(product)}
              onRemoveClick={() => handleRemove(product)}
              showSubTitle={false}
            />
          ))}
        </div>
      </>
    );
  };

  // Height scales with checkout container — distinct from other views for FLIP morph (see useFamilyTrayHeights)
  return (
    <div className="relative flex h-[var(--tray-h-product-selection,650px)] flex-col">
      <header className="relative flex flex-col gap-2 px-4 py-3 shrink-0">
        {/* Gradients aren't interpolable, so success state crossfades a green layer over the idle one */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: HEADER_IDLE_BG }}
        />
        <motion.div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: HEADER_SUCCESS_BG }}
          initial={false}
          animate={{ opacity: isComplete ? 1 : 0 }}
          transition={celebrateTransition}
        />
        <div className="relative flex gap-3 items-center py-1">
          <div className="relative w-12 h-12 flex items-center justify-center p-1 rounded-xl">
            <div
              aria-hidden
              className={classNames("absolute inset-0 rounded-xl",)}
              style={{ border: "1px solid var(--flo-primary-lighter-color)", background: isComplete ? "": TILE_IDLE_BG }}
            />
            <motion.div
              aria-hidden
              className="absolute inset-0 rounded-xl"
              style={{ background: TILE_SUCCESS_BG }}
              initial={false}
              animate={{ opacity: isComplete ? 1 : 0 }}
              transition={celebrateTransition}
            />
            <motion.span
              className="absolute inset-0 flex items-center justify-center"
              initial={false}
              animate={
                isComplete ? { opacity: 0, rotate: -45, } : { opacity: 1, rotate: 0 }
              }
              transition={{...celebrateTransition,delay:0.05,duration:0.5}}>
              <DiscountSealIcon/>
            </motion.span>
            <motion.span
              className="absolute inset-0 flex items-center justify-center"
              initial={false}
              animate={
                isComplete ? { opacity: 1, rotate: 0, scale: 1.3 } : { opacity: 0, rotate: 45, scale: 1 }
              }
              transition={{...celebrateTransition,delay:0.1,duration:0.5}}>
             <SuccessSealIcon />
            </motion.span>
          </div>
          <div className="flex flex-col gap-0.5">
            <motion.p
              key={isComplete ? "title-complete" : "title-idle"}
              initial={{ filter: "blur(4px)", opacity: 0 }}
              animate={{ filter: "blur(0px)", opacity: 1 }}
              transition={celebrateTransition}
              className={
                isComplete
                  ? "text-text-md-medium bg-gradient-to-r from-[#124f32] to-[#22744d] bg-clip-text text-transparent"
                  : "text-text-md-medium text-coal-dark"
              }>
              {isComplete ? (
                <>
                  <span className="font-bold">{couponName || "Discount"}</span> Unlocked!
                </>
              ) : (
                <>
                  Unlock <span className="font-bold">{couponName || "discount"}</span>
                </>
              )}
            </motion.p>
            <motion.p
              key={isComplete ? "subtitle-complete" : "subtitle-idle"}
              initial={{ filter: "blur(4px)", opacity: 0 }}
              animate={{ filter: "blur(0px)", opacity: 1 }}
              transition={celebrateTransition}
              className="text-text-xs-medium text-gray-dark">
              {isComplete
                ? "You've unlocked your exclusive discount"
                : remainingToShopMessage.map((part, index) => (
                    <span key={index} className={part.highlight ? "text-primary-dark font-semibold" : undefined}>
                      {part.text}
                    </span>
                  ))}
            </motion.p>
          </div>
        </div>
        {/* Completed — bar collapses away. Numeric height (track is a fixed 5px) interpolates smoothly
            where "auto" can't; -8px margin swallows the header's gap-2 so the header fully tightens */}
        <motion.div
          className=""
          initial={false}
          animate={
            isComplete ? { opacity: 0, height: 0, marginTop: -8 } : { opacity: 1, height: 5, marginTop: 0 }
          }
          transition={{ delay: isComplete ? 0.7 : 0 }}>
          <ProgressBar progress={progress} />
        </motion.div>
      </header>
      {showSelected ? (
        <section className="flex flex-col gap-2 flex-1 min-h-0">
          <div className="flex items-start px-4 py-1.5 shrink-0 w-full">
          <button
              type="button"
              onClick={() => setShowSelected(false)}
              className="bg-white flex min-h-[28px] items-center gap-1 px-1.5 rounded-md shadow-[0px_0px_0px_1px_rgba(10,13,18,0.1),0px_1px_2px_0px_rgba(10,13,18,0.05)] hover:bg-shade-4">
              <Icon IconComponent={CaretLeft} size="xxxs" iconProps={{ className: "text-iron",weight:"fill" }} />
              <span className="text-text-sm-medium text-iron whitespace-nowrap">Back to All</span>
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide pb-4">
            <div className="grid grid-cols-3 gap-4 px-4 w-full">
              {selectedProducts.map(({ blockIndex, product, variant, quantity }) => (
                <ProductCard
                  key={`${blockIndex}-${variant.variant_id}`}
                  imageUrl={getPrimaryImage(product)}
                  title={product.name}
                  subtitle={variant.name}
                  {...getVariantPricing(variant)}
                  quantity={quantity}
                  maxQuantity={variant.inventory_quantity}
                  onAddClick={() => handleSelectedQtyChange(blockIndex, variant, 1)}
                  onRemoveClick={() => handleSelectedQtyChange(blockIndex, variant, -1)}
                />
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="flex flex-col gap-2 flex-1 min-h-0">
          <SortFilterBar
            sortLabel={activeBlock ? getSortLabel(activeBlock.sortBy) : "Sort"}
            onSortClick={() => setOpenSheet("sort")}
            filterActive={Boolean(priceBand)}
            filterLabel={priceFilterLabel}
            onFilterClick={() => setOpenSheet("filter")}
            onFilterClear={handleFilterClear}
          />
          <div className="flex flex-1 min-h-0">
            {hasMultipleBlocks && (
              <div className="overflow-y-auto scrollbar-hide shrink-0  border-r border-black/[0.06]">
                <BlockNavRail
                  items={navItems}
                  activeId={String(activeBlockIndex)}
                  onSelect={(id) => actions.setActiveBlock(Number(id))}
                />
              </div>
            )}
            <div
              ref={scrollRef}
              className="flex flex-col gap-2 items-start flex-1 min-w-0 overflow-y-auto scrollbar-hide pb-4 overflow-x-hidden">
              {renderProducts()}
              {status === "ready" && !activeBlock?.refreshing && (
                <>
                  {loadingMore && <ProductGridSkeleton columns={hasMultipleBlocks ? 2 : 3} />}
                  {pageError && (
                    <button
                      type="button"
                      onClick={() => actions.fetchNextPage(activeBlockIndex)}
                      className="px-3 py-2 w-full text-center text-text-xs-medium text-gray-dark">
                      {pageError} · Tap to retry
                    </button>
                  )}
                  {hasMore && <div ref={sentinelRef} className="h-px w-full shrink-0" />}
                </>
              )}
            </div>
          </div>
        </section>
      )}
      {/* relative z-10 — footer's white fade shadow must paint above the grid, including the
          position:relative card image wrappers that would otherwise sit on top of it */}
      <footer className="relative z-10 shrink-0">
        <SelectionFooter
          selectedCount={selectedCount}
          thumbnails={
            recentImages.length > 0
              ? recentImages
              : activeBlock?.block.products[0]
              ? [getPrimaryImage(activeBlock.block.products[0])]
              : []
          }
          isEnabled={footerEnabled}
          ctaLabel={footerCtaLabel}
          isLoading={isSubmitting}
          onAddSelected={handleFooterCta}
          onThumbnailsClick={() => setShowSelected(true)}
          isSelectedViewOpen={showSelected}
        />
      </footer>

      <SortSheet
        open={openSheet === "sort"}
        value={activeBlock?.sortBy ?? "GAP_CLOSING"}
        onSelect={handleSortSelect}
        onClose={() => setOpenSheet(null)}
      />
      <FilterSheet
        open={openSheet === "filter"}
        min={priceBand?.min}
        max={priceBand?.max}
        onApply={handleFilterApply}
        onClose={() => setOpenSheet(null)}
      />
    </div>
  );
};
